import { useQueryClient } from '@tanstack/react-query';
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ChatResponsePanel } from '@/lib/chat/ChatResponsePanel';
import { SlideoutOriginProvider } from '@/lib/crm/contexts/SlideoutOriginContext';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { logger } from '@/lib/logger';
import { NoOpDismissProvider } from '@/lib/proposals/hooks/NoOpDismissContext';
import { ProposalPreviewProvider } from '@/lib/proposals/hooks/ProposalPreviewProvider';
import { useProposalChatProgress } from '@/lib/proposals/hooks/useProposalChatProgress';
import { useShouldHideProposalFooter } from '@/lib/proposals/hooks/useShouldHideProposalFooter';
import {
  onChatTurnUpdatedWs,
  setProposalHidden,
  setProposalVisible,
  trackChatTurn,
} from '@/lib/proposals/realtime/chatTaskTracker';
import { useTrackedChatTurn } from '@/lib/proposals/realtime/useTrackedChatTurn';
import { findActiveProcessingChain } from '@/lib/reasoning/chainStatus';
import { useViewHealthMarker } from '@/lib/view-health';
import { cx } from '@/utils/cx';

import { useProposalSidebarOptional } from '../../contexts/ActionSidebarContext';
import type { ProposalDetail } from '../../lib/proposals/core/types';
import {
  ProposalSelectionProvider,
  useProposalSelection,
} from '../../lib/proposals/hooks/ProposalSelectionContext';
import { useDiscussionConversation } from '../../lib/proposals/hooks/useDiscussionConversation';
import { useMergedActions } from '../../lib/proposals/hooks/useMergedActions';
import { useProposal } from '../../lib/proposals/hooks/useProposal';
import { useProposalChat } from '../../lib/proposals/hooks/useProposalChat';
import { ChatErrorBanner } from './ChatErrorBanner';
import { ProposalClarifyingQuestions } from './clarifying/ProposalClarifyingQuestions';
import {
  encodeEmailThreadEntryId,
  findEmailActionForThread,
} from './emailThreadEntryId';
import { SelectionAwareExecutionStatusBanners } from './ExecutionStatusBanners';
import { IndeterminateResolveBanner } from './IndeterminateResolveBanner';
import { PartialExecutionBanner } from './PartialExecutionBanner';
import { ProposalContent } from './ProposalContent';
import ProposalDetailHeader from './ProposalDetailHeader';
import { ReopenControl } from './ReopenControl';
import {
  AutoSaveCoordinationProvider,
  AutoSaveNavigationGuard,
  EmailTrackingProvider,
  isWelcomeProposal,
} from './shared';
import StickyFooter from './StickyFooter';
import { TriggerShortcutButtons } from './TriggerShortcutButtons';
import { useAccumulatingActionUpdate } from './useAccumulatingActionUpdate';
import {
  ProposalRefreshProvider,
  useProposalRefresh,
} from './useProposalRefresh';
import { useTriggerShortcutButtons } from './useTriggerShortcutButtons';
import WelcomeProposalContent from './WelcomeProposalContent';
import { WithdrawalExplanation } from './WithdrawalExplanation';

// Visible user-bubble message sent to kick off the refine-after-clarifying-answers
// agent run. Keep it presentable — it surfaces in ChatResponsePanel.
// #refine-routing: backend will route this message to the clarifying-answers
// refinement path; replace only this constant when the mechanism changes.
const CLARIFYING_REFINE_MESSAGE =
  'Here are my answers to your questions – please refine the action.';

// Stable no-op fallback for notifyProposalUpdated when rendered outside
// ProposalSidebarProvider — must be a stable reference for use in effect
// dependency arrays.
const NOOP = () => {};

/**
 * Builds the clarifying-questions card node (content-region, not footer —
 * see call site). Product decision 2026-09-01 (task 4): structured
 * clarifying questions render as a card under the Action Overview, NOT in
 * the footer — the footer holds only the chat input and the approve/reject
 * buttons. Onboarding proposals never show clarifying questions (parity
 * with the former footer gating).
 */
function buildClarifyingPanelNode(
  currentAction: ProposalDetail,
  isReadOnly: boolean,
  isProcessingChat: boolean,
  onSubmitAnswers: () => void
): ReactNode {
  const isStructuredClarifying =
    !!currentAction.clarifying_questions?.questions?.length;
  const isOnboardingProposal = currentAction.actions.some(
    a => a.proposed_action_type_name === 'onboarding_action'
  );
  return isStructuredClarifying && !isReadOnly && !isOnboardingProposal ? (
    <ProposalClarifyingQuestions
      proposal={currentAction}
      editable={currentAction.resolution === 'pending' && !isProcessingChat}
      onSubmitAnswers={onSubmitAnswers}
    />
  ) : null;
}

/**
 * Thin wrapper that consumes ProposalSelectionContext for the footer.
 * Re-renders when selection changes, but ProposalDetailPanelInner does NOT.
 * Defined outside the component so React treats it as a stable component.
 */
const SelectionAwareStickyFooter = React.memo(
  function SelectionAwareStickyFooter(
    props: Omit<
      React.ComponentProps<typeof StickyFooter>,
      'selectedActionIds' | 'onSuccess'
    > & {
      onSuccess?: (selectedActionIds?: number[]) => void;
    }
  ) {
    const { selectedActionIds, noOpActionIds } = useProposalSelection();
    const { mergedItems } = useMergedActions(props.action.actions);
    const { onSuccess, ...rest } = props;

    const handleSuccess = useCallback(() => {
      onSuccess?.(selectedActionIds);
    }, [onSuccess, selectedActionIds]);

    // Single-action proposals predate selection gating: passing `undefined`
    // keeps StickyFooter's "zero selected -> disable Approve" check from
    // firing for a lone action that was never selection-gated (it has no
    // checkbox UI to deselect it with). But a single action can still be a
    // fully no-op update — auto-deselected and hidden by
    // ProposalSelectionProvider — and that genuinely-empty selection must
    // still disable Approve. Gate on real emptiness (multi-action, or a
    // no-op single action) rather than on raw action count.
    const soleAction =
      props.action.actions.length === 1 ? props.action.actions[0] : undefined;
    const gateOnSelection =
      props.action.actions.length > 1 ||
      (soleAction !== undefined && noOpActionIds.includes(soleAction.id));

    return (
      <StickyFooter
        {...rest}
        selectedActionIds={gateOnSelection ? selectedActionIds : undefined}
        mergedItems={mergedItems}
        onSuccess={onSuccess ? handleSuccess : undefined}
      />
    );
  }
);

interface ProposalDetailPanelProps {
  action: ProposalDetail;
  onClose: () => void;
  onUpdate: (action: ProposalDetail) => void;
  onComplete: (precomputedNextActionId?: number) => void;
  getNextActionId?: (currentId: number) => number | null;
  /**
   * Skip the panel's own header. For hosts whose CHROME renders the header
   * row — the proposal slideout puts `ProposalDetailHeader` in
   * `SlideoutMenu`'s header slot so the row (and its close button) exists in
   * every state, not only once the proposal has loaded.
   */
  hideHeader?: boolean;
  onGuardChange?: (guard: ((action: () => void) => boolean) | null) => void;
  /**
   * Left-most header content, before the back chevron. Only rendered when
   * `variant` is `'page'`. The full-page action route passes its actions-list
   * sidebar toggle here.
   */
  headerLeading?: ReactNode;
  /**
   * Which surface hosts this panel — see ProposalDetailHeader's `variant`.
   * `'pane'` (default) keeps the back chevron mobile-only, because the list
   * is already visible beside the detail. `'page'` shows it at every size.
   */
  variant?: 'pane' | 'page';
  /**
   * Skip the panel's own discussion chat (the footer's refine input and the
   * agent-response strip). For hosts that already ARE a chat — the docked
   * pane inside a conversation — where a second input asking the same agent
   * would split the thread. Approve/Reject stay; only the chat goes.
   */
  hideChat?: boolean;
}

/**
 * Inner component that uses the useProposalRefresh hook.
 * Must be wrapped by ProposalRefreshProvider.
 */
function ProposalDetailPanelInner({
  action,
  onClose,
  onUpdate,
  onComplete,
  getNextActionId,
  hideHeader,
  headerLeading,
  variant,
  hideChat,
}: {
  action: ProposalDetail;
  onClose: () => void;
  onUpdate: (action: ProposalDetail) => void;
  onComplete: (precomputedNextActionId?: number) => void;
  getNextActionId?: (currentId: number) => number | null;
  hideHeader?: boolean;
  headerLeading?: ReactNode;
  variant?: 'pane' | 'page';
  hideChat?: boolean;
}) {
  // State
  // Check if this is the welcome import proposal (special handling: no chat, custom footer)
  // Defined early so it's available for ResizeObserver effect dependency
  const isWelcome = isWelcomeProposal(action);
  const { openSlideout, closeFromEntry, stack } = useSlideoutStack();

  // Whether the chat tracker still has an in-flight turn for this proposal —
  // drives the in-progress indicator independent of whether this panel
  // initiated the turn (e.g. rehydrated after a background completion).
  const isTurnTracked = useTrackedChatTurn(action.id);

  // Register this proposal as visible while mounted so the tracker suppresses
  // its own success toast (the mounted-path reload below handles the update
  // instead). Synchronous + keyed on action.id: ProposalSidebar reuses a
  // single ProposalDetailPanel instance across proposals, so this must run
  // before paint on every proposal switch.
  useLayoutEffect(() => {
    setProposalVisible(action.id);
    return () => setProposalHidden(action.id);
  }, [action.id]);

  // Whether the user has dismissed the agent-response slide-up panel for
  // the current chain. Re-set whenever a new task starts (see effect below).
  const [responseDismissed, setResponseDismissed] = useState(false);
  const handleDismissResponse = useCallback(() => {
    setResponseDismissed(true);
  }, []);

  // The proposal whose gating clarifying-answers refine turn is currently in
  // flight, or null. Scoped to a specific proposal id — NOT a bare boolean —
  // because ProposalSidebar reuses ONE ProposalDetailPanel instance across
  // proposals (see the visibility layout effect above): a refine started on
  // proposal A must not carry into proposal B when the sidebar switches, or B
  // would be reported as `refining` and its Approve wrongly stays blocked
  // during B's own ordinary chat turn.
  const [refiningProposalId, setRefiningProposalId] = useState<number | null>(
    null
  );
  // Latch so the flag clears only after the turn has actually started and then
  // finished — not on the render between setting it and isProcessingChat
  // flipping true.
  const refineSawProcessingRef = useRef(false);
  // `clarifyingRefineInFlight` is derived below, once `activeChain` is
  // available — it must also engage the lock when the refine was started in
  // another view or before a reload (this panel then mounts with
  // refiningProposalId === null), which is recoverable only from server chain
  // data, not from the local latch.
  // The most recent message the user sent — captured here at submit time
  // so it persists past `clearPolling()` (which clears `optimisticMessage`)
  // and stays visible at the top of the response panel until the next send
  // overwrites it.
  const [panelUserMessage, setPanelUserMessage] = useState<string | null>(null);

  // Get triggerRefresh from context (moved up so it's available for callbacks)
  const { triggerRefresh } = useProposalRefresh();

  // Get notifyProposalUpdated to signal conversation pages to reload
  // Optional - only available when rendered within ProposalSidebarProvider (e.g., in ProposalSidebar)
  // When not available (e.g., in ProposalCenter), provides no-op fallback
  const sidebarContext = useProposalSidebarOptional();
  const notifyProposalUpdated = sidebarContext?.notifyProposalUpdated ?? NOOP;

  // Stable callback for useProposalChat — updates parent with conversation ID
  const handleConversationCreated = useCallback(
    (newConversationId: number) => {
      if (!action.discussion_conversation) {
        onUpdate({
          ...action,
          discussion_conversation: newConversationId,
        });
      }
    },
    [action, onUpdate]
  );

  const queryClient = useQueryClient();
  const { data: discussionDetail } = useDiscussionConversation(
    action.org,
    action.discussion_conversation
  );
  const activeChain = discussionDetail
    ? findActiveProcessingChain(discussionDetail.chains)
    : null;
  const resumeTaskId = activeChain?.task_id ?? null;

  // Chat hook - handles message state, API calls, and draft persistence
  const {
    message: chatMessage,
    setMessage: setChatMessage,
    optimisticMessage,
    isSending,
    error: chatError,
    sendMessage,
    retryLastMessage,
    clearError: clearChatError,
    taskId,
    conversationId,
    clearPolling,
  } = useProposalChat({
    orgId: action.org,
    proposalId: action.id,
    onConversationCreated: handleConversationCreated,
    resumeTaskId,
    resumeConversationId: action.discussion_conversation,
  });

  // Register the in-flight turn with the tracker. Covers turn start, the
  // rehydrate seed (resumeTaskId), and supersede (a 2nd send before the 1st
  // resolves) — taskId transitions null→value or value→value in each case.
  useEffect(() => {
    if (taskId && conversationId != null) {
      trackChatTurn({
        orgId: action.org,
        proposalId: action.id,
        conversationId,
        taskId,
        queryClient,
      });
    }
  }, [taskId, conversationId, action.org, action.id, queryClient]);

  // Direct subscription to React Query cache - ensures re-render when cache updates
  // after AI chat completion, regardless of whether parent re-renders.
  // Only used for rendering; initialization and callbacks use the `action` prop.
  const { data: queryAction } = useProposal(action.org, action.id);

  // Use query data for rendering when available, fall back to prop for initial render
  const currentAction = queryAction ?? action;

  // "View X" trigger shortcut buttons — rendered in the header's right side
  // (they were removed from the collapsed Action Overview).
  const {
    isLoading: isLoadingTriggerButtons,
    buttons: triggerButtons,
    onActivate: activateTriggerButton,
  } = useTriggerShortcutButtons(currentAction);

  // View-health: fire `proposal-actions-rendered` once the detail panel has
  // committed. `useViewHealthMarker` fires in a `useEffect`, so by the time it
  // runs, React has committed the entire subtree (including the action cards
  // rendered by `ProposalContent` below). On the `actions/*` route this
  // satisfies part of the PROPOSAL_LOADED variant alongside
  // `proposal-detail-ready` (see dashboard.routes.tsx). In non-actions
  // contexts (e.g. ProposalSidebar on other routes) the marker is a no-op —
  // the current scope's variants don't reference it.
  //
  // Custom-UI proposals (rendered via ProposalUIRenderer, no `actions` array)
  // still fire the marker: the panel IS the full render for those proposals.
  useViewHealthMarker('proposal-actions-rendered', { ready: true });

  // Resolved proposals (anything except pending/executing) render in read-only mode:
  // no edit buttons, no checkboxes, no approve/reject, no chat.
  // Derived from currentAction (not prop) so it stays in sync with cache updates.
  const isReadOnly =
    currentAction.resolution !== 'pending' &&
    currentAction.resolution !== 'executing';

  // Selection state is managed by ProposalSelectionProvider (wraps the render tree).
  // This avoids re-rendering this entire 800+ line component on every checkbox toggle.

  // Handle action updates using a functional updater pattern. Sequential updates
  // within a single async save (e.g. a merged create card persisting the primary
  // record and a swapped association in separate awaited calls) accumulate
  // instead of reverting each other — see useAccumulatingActionUpdate.
  const handleActionUpdate = useAccumulatingActionUpdate(action, onUpdate);

  // Handle success callback from StickyFooter
  // Now simplified: toast is shown by StickyFooter, we just navigate to next action
  const handleSuccess = useCallback(() => {
    // Capture the next action ID immediately, BEFORE any list updates can occur
    // This ensures we get the correct "next" item based on current list state
    const nextId = getNextActionId?.(action.id) ?? null;

    logger.debug('Action completed, navigating to next', {
      proposal_id: action.id,
      next_action_id: nextId,
      component: 'proposals',
    });

    // Navigate to next action (or list if no more actions)
    onComplete(nextId ?? undefined);
  }, [onComplete, getNextActionId, action.id]);

  // Reset the response-panel dismissed flag whenever a new chain starts —
  // a new chain produces a new response, the user should see it.
  useEffect(() => {
    if (taskId) {
      setResponseDismissed(false);
    }
  }, [taskId]);

  // Reset the captured user message when switching proposals. ProposalSidebar
  // reuses a single ProposalDetailPanel instance across proposals (no key
  // prop), so without this the previous proposal's prompt would appear at
  // the top of the next proposal's response panel.
  useEffect(() => {
    setPanelUserMessage(null);
  }, [action.id]);

  // Rehydrate the in-flight prompt on resume: when a live turn is re-derived
  // from server chain data (switch-back or refresh), restore the user's
  // message at the top of the response panel. Functional update so a message
  // captured locally at submit time always wins; declared after the action.id
  // reset above so a same-commit proposal switch clears before seeding.
  const resumeUserMessage = activeChain?.user_message?.trim() || null;
  useEffect(() => {
    if (resumeUserMessage) {
      setPanelUserMessage(prev => prev ?? resumeUserMessage);
    }
  }, [resumeUserMessage]);

  // Poller callbacks are now short-circuits into the tracker — the tracker
  // owns terminal handling (cache write + toasts) so it survives unmount.
  // handlePollComplete's body MUST stay exactly this short-circuit call: the
  // tracker's once-guard (resolved flag) only protects the tracker path, not
  // a second write from here.
  const handlePollComplete = useCallback(() => {
    if (taskId) {
      onChatTurnUpdatedWs(action.id, taskId);
    }
    // taskId MUST stay in this dep array: the WS `chain.completed` event
    // carries no `task_id`, so correctness for superseded turns relies on
    // this closure holding the current taskId (see chatTaskTracker.ts:246).
    // A future lint-driven dep cleanup must not drop it.
  }, [action.id, taskId]);
  const handlePollError = useCallback(() => {
    // Tracker owns terminal failure via its own poll/watchdog (transient-safe
    // to a mount/unmount race). Intentionally no-op.
  }, []);

  // Progress hook - triggered when taskId is set by useProposalChat. Kept for
  // `latestStep` (live "agent thinking" display) while mounted.
  const { latestStep } = useProposalChatProgress(
    taskId,
    conversationId?.toString() || null,
    action.org?.toString(),
    {
      onComplete: handlePollComplete,
      onError: handlePollError,
    }
  );

  // Combined processing state: sending API request OR the tracker has an
  // in-flight turn for this proposal OR a taskId was just assigned (covers
  // the render before the tracker registration effect above has run).
  const isProcessingChat = isSending || isTurnTracked || !!taskId;

  // The gating `refining` lock engages only for the proposal whose clarifying
  // refine turn is in flight. Two independent signals, OR'd, so the lock holds
  // whether or not THIS panel started the turn:
  //  - refiningProposalId: the local latch, set when this instance kicks off
  //    the refine (covers the optimistic window before server data catches up);
  //  - the active processing chain being the clarifying refine turn — the only
  //    signal that survives a panel remount or a reload started by another
  //    view, where refiningProposalId is null (reviewer #13843).
  // Message-scoped (not "any chat turn") so a later ordinary turn on the same
  // proposal does not mislabel itself as `refining` and re-lock approval.
  //
  // The chain-derived fallback is gated on `isProcessingChat` so it expires with
  // the tracked run: when the refine watchdog (MAX_WALL_MS) times out it clears
  // local processing (`isTurnTracked`→false, then `clearPolling` drops taskId),
  // but the discussion-chain cache is NOT refetched on timeout, so a bare
  // `user_message === CLARIFYING_REFINE_MESSAGE` read would keep this true — and
  // Approve disabled — indefinitely. Requiring `isProcessingChat` releases the
  // lock the moment the tracked run ends (reviewer #13843, cubic P1). The seed
  // effect in useProposalChat won't re-seed the finished task, so this can't loop.
  //
  // The chain must also be the turn CURRENTLY in flight, not merely a cached
  // refine chain: after a timed-out refine the cache still holds that chain
  // (never refetched), so a later ordinary turn — which flips isProcessingChat
  // true again — would otherwise match the stale refine `user_message` and
  // re-lock Approve. `taskId` is the live turn once useProposalChat has it;
  // during the one-render seed lag on a remount it is still null, so fall back
  // to `resumeTaskId` (== activeChain.task_id), but only when not sending so an
  // ordinary turn's pre-taskId window (isSending true, taskId null) can't match
  // the stale refine chain (reviewer #13843, cubic P2).
  const refineChainInFlight =
    isProcessingChat &&
    activeChain?.user_message?.trim() === CLARIFYING_REFINE_MESSAGE &&
    (activeChain?.task_id === taskId ||
      (!isSending && taskId == null && activeChain?.task_id === resumeTaskId));
  const clarifyingRefineInFlight =
    refiningProposalId === action.id || refineChainInFlight;

  // Clear the clarifying-refine flag once that turn completes. Only observe the
  // proposal that STARTED the refine: after a sidebar switch, isProcessingChat
  // reflects the newly-viewed proposal, so we must not latch or clear based on
  // it while viewing a different proposal (that would either lose the latch and
  // leave the refining proposal stuck locked on return, or clear the flag
  // against the wrong proposal's turn). Wait until the turn has been observed in
  // flight (refineSawProcessingRef) before clearing, so a one-render lag between
  // setting the flag and isProcessingChat flipping true can't drop it early.
  useEffect(() => {
    if (refiningProposalId == null) {
      refineSawProcessingRef.current = false;
      return;
    }
    if (refiningProposalId !== action.id) {
      // Viewing a different proposal than the one refining — leave the latch and
      // the flag untouched until the user returns to the refining proposal.
      return;
    }
    if (isProcessingChat) {
      refineSawProcessingRef.current = true;
    } else if (refineSawProcessingRef.current) {
      refineSawProcessingRef.current = false;
      setRefiningProposalId(null);
    }
  }, [refiningProposalId, action.id, isProcessingChat]);

  // Mounted-path reload: when the tracker finalizes a turn while this panel
  // is mounted, clear the local poll, reload the version timeline, and
  // reload the reasoning chat chains. Guarded by a ref so it fires only on
  // the tracked→untracked edge (not on every render while untracked).
  const wasTrackedRef = useRef(false);
  useEffect(() => {
    if (wasTrackedRef.current && !isTurnTracked) {
      clearPolling(); // clear local taskId → stop the mounted poll
      void triggerRefresh(); // reload ActionVersionTimeline via onRefresh subscribers
      notifyProposalUpdated(); // reload ReasoningChat chains (no-op fallback outside ProposalSidebar)
    }
    wasTrackedRef.current = isTurnTracked;
  }, [isTurnTracked, clearPolling, triggerRefresh, notifyProposalUpdated]);

  // Find the email action for the thread panel
  const emailActionForThread = findEmailActionForThread(action);

  // Handlers
  const handleOpenThread = useCallback(() => {
    if (!emailActionForThread) {
      return;
    }
    // Toggle: EmailTriggerCard highlights "View thread" via kind-presence
    // (any email_thread entry on the stack — see EmailTriggerCard.tsx:108-113),
    // so mirror that predicate here. Close the entry actually on the stack —
    // its id may differ from what this proposal would encode.
    const openThreadEntry = stack.find(entry => entry.kind === 'email_thread');
    if (openThreadEntry) {
      closeFromEntry(openThreadEntry);
      return;
    }
    // App-level open on the unified slideout stack: a "View thread" click
    // from the proposal view REPLACES whatever record is already layered
    // above the proposal (only clicks from INSIDE a slideout build the
    // stack). Entity badges rendered inside the thread still layer further
    // slideouts on top via pushSlideout.
    openSlideout(
      {
        kind: 'email_thread',
        id: encodeEmailThreadEntryId(action.id, emailActionForThread.id),
      },
      { base: { kind: 'proposal', id: String(action.id) } }
    );
  }, [openSlideout, closeFromEntry, stack, action.id, emailActionForThread]);

  const handleChatSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!isProcessingChat) {
        const trimmed = chatMessage.trim();
        if (trimmed) {
          setPanelUserMessage(trimmed);
        }
        sendMessage(chatMessage);
      }
    },
    [chatMessage, sendMessage, isProcessingChat]
  );

  // Kicks off the discuss/reasoning rail after clarifying answers have been
  // autosaved. Mirrors handleChatSubmit but uses the fixed refinement message
  // so the backend can route it to the clarifying-answers path.
  // #refine-routing: replace CLARIFYING_REFINE_MESSAGE when backend routing changes.
  const handleSubmitClarifyingAnswers = useCallback(() => {
    if (isProcessingChat) {
      return;
    }
    // Mark this turn as the clarifying refine so the gating lock shows
    // `refining` for this proposal (and only it) until it completes.
    setRefiningProposalId(action.id);
    setPanelUserMessage(CLARIFYING_REFINE_MESSAGE);
    sendMessage(CLARIFYING_REFINE_MESSAGE);
  }, [isProcessingChat, sendMessage, action.id]);

  // ── Clarifying questions (content-region card) ──────────────────────────
  // See buildClarifyingPanelNode for the rendering rules.
  const clarifyingPanelNode = buildClarifyingPanelNode(
    currentAction,
    isReadOnly,
    isProcessingChat,
    handleSubmitClarifyingAnswers
  );

  const contentRegionRef = useRef<HTMLDivElement>(null);
  const shouldHideFooter = useShouldHideProposalFooter(contentRegionRef);

  // Origin for useOpenSlideout: the proposal panel behaves like the app —
  // a click anywhere in it (header buttons, action-card badges, markdown
  // entity links) REPLACES whatever record is layered above this proposal
  // instead of stacking; the proposal itself stays underneath when it is a
  // stack entry (the entry simply isn't found when it's a page/pane host,
  // so the whole stack is replaced). Provided in EVERY variant.
  const slideoutOriginEntry = useMemo(
    () => ({ kind: 'proposal' as const, id: String(action.id) }),
    [action.id]
  );

  return (
    <SlideoutOriginProvider entry={slideoutOriginEntry}>
      <ProposalSelectionProvider
        actions={currentAction.actions}
        proposalId={action.id}
        orgId={action.org}
        triggerRefresh={triggerRefresh}
      >
        <ProposalPreviewProvider resolution={currentAction.resolution}>
          <NoOpDismissProvider>
            <div className='h-full bg-bg-secondary flex flex-col overflow-hidden relative w-full'>
              {!hideHeader && (
                <div className='bg-bg-secondary relative z-20'>
                  <ProposalDetailHeader
                    action={currentAction}
                    onClose={onClose}
                    leading={headerLeading}
                    variant={variant}
                    trailing={
                      <TriggerShortcutButtons
                        isLoading={isLoadingTriggerButtons}
                        buttons={triggerButtons}
                        orgId={currentAction.org}
                        onActivate={activateTriggerButton}
                        className='flex-nowrap'
                      />
                    }
                  />
                </div>
              )}

              {isWelcome ? (
                <WelcomeProposalContent
                  proposal={action}
                  onUpdate={onUpdate}
                  onComplete={onComplete}
                  getNextActionId={getNextActionId}
                />
              ) : (
                <>
                  <IndeterminateResolveBanner
                    orgId={action.org}
                    proposalId={action.id}
                    actions={currentAction.actions}
                    onResolved={triggerRefresh}
                  />
                  <PartialExecutionBanner proposal={currentAction} />
                  {/* Already-sent + failed-execution banners — content-region
                    banner stack, not the footer (task 4 part 2a). Selection-
                    aware: deselecting a failed action lifts its verdict.
                    Gated !isReadOnly to preserve the footer-era visibility:
                    these warn during an actionable state (pending retry /
                    executing), not on every resolved email in history. */}
                  {!isReadOnly && (
                    <SelectionAwareExecutionStatusBanners
                      action={currentAction}
                    />
                  )}
                  {currentAction.resolution === 'withdrawn' && (
                    <WithdrawalExplanation
                      executionResult={currentAction.execution_result}
                    />
                  )}
                  {/* Chat send-failure banner — content-region banner stack, not
                    the footer (task 4 part 2b). */}
                  {!isReadOnly && (
                    <ChatErrorBanner
                      error={chatError}
                      isRetrying={isSending}
                      onRetry={retryLastMessage}
                      onClear={clearChatError}
                    />
                  )}
                  <div
                    ref={contentRegionRef}
                    className='flex-1 flex flex-col min-h-0 relative bg-[var(--color-bg-secondary)]'
                  >
                    <ProposalContent
                      proposal={currentAction}
                      onUpdate={onUpdate}
                      onActionUpdate={handleActionUpdate}
                      isProcessing={isProcessingChat}
                      onOpenThread={
                        emailActionForThread ? handleOpenThread : undefined
                      }
                      optimisticMessage={optimisticMessage}
                      readOnly={isReadOnly}
                      clarifyingRefineInFlight={clarifyingRefineInFlight}
                      clarifyingPanel={clarifyingPanelNode}
                    />
                  </div>

                  {/* Footer — chat input lives inside StickyFooter (hidden for resolved proposals).
                The ChatResponsePanel is the single home for what the agent
                is doing/saying. While the chain runs it shows a constant
                "Working on it…" header (no close button). After the chain
                settles it shows a "Done" header and persists as the agent's
                response until dismissed or a new chain starts. */}
                  {!isReadOnly && (
                    <div
                      className={cx(
                        'flex-shrink-0',
                        shouldHideFooter && 'hidden'
                      )}
                      data-testid='proposal-footer'
                    >
                      {!responseDismissed && !hideChat && (
                        <ChatResponsePanel
                          content={latestStep?.content}
                          userMessage={panelUserMessage ?? undefined}
                          isLive={isProcessingChat}
                          onDismiss={handleDismissResponse}
                          orgId={action.org}
                        />
                      )}
                      <SelectionAwareStickyFooter
                        action={currentAction}
                        onUpdate={onUpdate}
                        onComplete={onComplete}
                        onSuccess={handleSuccess}
                        getNextActionId={getNextActionId}
                        chatMessage={chatMessage}
                        // The footer renders its refine input only when BOTH chat
                        // callbacks are present — withholding them is the
                        // sanctioned way to drop the input while keeping
                        // Approve/Reject (see StickyFooter's leftCellContent gate).
                        onChatChange={hideChat ? undefined : setChatMessage}
                        onChatSubmit={hideChat ? undefined : handleChatSubmit}
                        isSendingChat={isSending}
                        isProcessingChat={isProcessingChat}
                        clarifyingRefineInFlight={clarifyingRefineInFlight}
                      />
                    </div>
                  )}
                  {isReadOnly && currentAction.resolution === 'rejected' && (
                    <div className='flex-shrink-0'>
                      <ReopenControl
                        orgId={currentAction.org}
                        proposalId={currentAction.id}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Email thread is now rendered by `SlideoutRenderer` when its
            entry is on the slideout stack — see `handleOpenThread` above. */}
            </div>
          </NoOpDismissProvider>
        </ProposalPreviewProvider>
      </ProposalSelectionProvider>
    </SlideoutOriginProvider>
  );
}

/**
 * Proposal detail panel that displays action details, version history, and chat input.
 * Wraps content with ProposalRefreshProvider to enable cache invalidation and subscriber notifications.
 */
export default function ProposalDetailPanel({
  action,
  onClose,
  onUpdate,
  onComplete,
  getNextActionId,
  hideHeader,
  onGuardChange,
  headerLeading,
  variant,
  hideChat,
}: ProposalDetailPanelProps) {
  return (
    <ProposalRefreshProvider orgId={action.org} proposalId={action.id}>
      <AutoSaveCoordinationProvider>
        <EmailTrackingProvider>
          <AutoSaveNavigationGuard onGuardChange={onGuardChange} />
          <ProposalDetailPanelInner
            action={action}
            onClose={onClose}
            onUpdate={onUpdate}
            onComplete={onComplete}
            getNextActionId={getNextActionId}
            hideHeader={hideHeader}
            headerLeading={headerLeading}
            variant={variant}
            hideChat={hideChat}
          />
        </EmailTrackingProvider>
      </AutoSaveCoordinationProvider>
    </ProposalRefreshProvider>
  );
}
